import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const Label = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const InputField = styled.input`
  padding: 0.75rem 1rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  border: 1px solid ${props => props.error ? props.theme.colors.error : props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text.primary};
  transition: all 0.2s ease-in-out;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? props.theme.colors.error : props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.error ? props.theme.colors.error + '20' : props.theme.colors.primary + '20'};
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.text.tertiary};
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.text.tertiary};
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  border: 1px solid ${props => props.error ? props.theme.colors.error : props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text.primary};
  transition: all 0.2s ease-in-out;
  width: 100%;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? props.theme.colors.error : props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.error ? props.theme.colors.error + '20' : props.theme.colors.primary + '20'};
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.text.tertiary};
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.text.tertiary};
  }
`;

const ErrorMessage = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.error};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const HelpText = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const InputComponent = ({
  label,
  error,
  helpText,
  multiline = false,
  rows = 3,
  ...props
}) => {
  const { theme } = useTheme();
  
  return (
    <InputContainer>
      {label && <Label theme={theme}>{label}</Label>}
      {multiline ? (
        <TextArea
          theme={theme}
          error={error}
          rows={rows}
          {...props}
        />
      ) : (
        <InputField
          theme={theme}
          error={error}
          {...props}
        />
      )}
      {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}
      {helpText && !error && <HelpText theme={theme}>{helpText}</HelpText>}
    </InputContainer>
  );
};

export default InputComponent;

